import { useConnection } from "@solana/wallet-adapter-react";
import type { FarmInfoDataMap } from "@stepfinance/farms/src/lib/info";
import type { OrcaFarmInfo } from "@stepfinance/farms/src/lib/info/orca/types";
import type { OrcaFarmPosition } from "@stepfinance/farms/src/lib/positions/orca";
import { loadOrcaFarmPositions } from "@stepfinance/farms/src/lib/positions/orca";
import type { TokenAccount } from "@stepfinance/tokens";
import { useEffect, useMemo, useState } from "react";

import { useAccountsContext } from "../../contexts/accounts";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLiquidity } from "../../contexts/liquidity";
import { useMarkets } from "../../contexts/market";
import { getTokenMap } from "../../utils/tokenInfo";

export type AquafarmUserTokenAccount = OrcaFarmInfo & {
  tokenAccount: TokenAccount;
};

export function useOrcaFarms(orcaFarmInfoMap: FarmInfoDataMap<"Orca">) {
  const { connection } = useConnection();
  const { connected, publicKey } = useEnrichedWallet();
  const { marketsInitialised, priceInUSD, refreshing: marketsRefreshing } = useMarkets();
  const { userTokenAccounts } = useAccountsContext();
  const { orcaPools } = useLiquidity();
  const [farms, setFarms] = useState<OrcaFarmPosition[]>([]);

  const tokenAccounts = useMemo(
    () => userTokenAccounts.map((acc) => ({ mint: acc.info.mint, pubkey: acc.pubkey })),
    [userTokenAccounts],
  );

  useEffect(() => setFarms([]), [connected]);

  useEffect(() => {
    const marketsRefreshFinished = !marketsRefreshing;
    if (
      publicKey &&
      marketsInitialised &&
      marketsRefreshFinished &&
      Object.values(orcaPools).length > 0 &&
      Object.values(orcaFarmInfoMap).length > 0
    ) {
      void loadOrcaFarmPositions(
        publicKey,
        connection,
        getTokenMap(),
        priceInUSD,
        tokenAccounts,
        orcaPools,
        orcaFarmInfoMap,
      ).then(setFarms);
    }
  }, [
    orcaPools,
    connection,
    connected,
    marketsInitialised,
    marketsRefreshing,
    priceInUSD,
    tokenAccounts,
    publicKey,
    orcaFarmInfoMap,
  ]);

  return farms;
}
